

import { Component, Emit, Mixins, Prop, VModel, Watch } from "vue-property-decorator";
import IBDialogContainer from "./IBDialogContainer.vue";
import IBSVGIconClose from "../SVGIcon/IBSVGIconClose.vue";
import IBButton from "../IBButton/IBButton.vue";
import IBCountdown from "../IBCountdown/IBCountdown.vue";
import IBInputField from "../TextField/IBInputField.vue";
import { $generateSms, $verifySms } from '@/api/sms/index';
import { GenerateSmsData, GenerateSmsInitiator, GenerateSmsOperationType, GenerateSmsResultData, VerifySmsData } from "@/api/sms/types";
import { PROFILE_MODULE_NAME } from "@/store/modules/profile";
import { IProfileEntity } from "@/models/modules/profile/interface";
import { Getter } from "vuex-class";
import { ICompany } from "@/api/mainPage/company/interface";
import MaskList from "@/modules/Mask/MaskList";
import IBDialogFieldFocusing from "./mixins/IBDialogFieldFocusing";

const DEFAULT_REMAINING_TIME = 90;

@Component({
	components: {
		IBDialogContainer,
		IBSVGIconClose,
		IBButton,
		IBCountdown,
		IBInputField
	}
})
export default class IBDialogSms extends Mixins(
	IBDialogFieldFocusing
) {

	$refs!: {
		input: IBInputField
		smsCode?: IBCountdown
	}

	@VModel({ type: Boolean })
	model!: boolean

	@Prop(String)
	readonly title?: string

	@Prop(String)
	readonly operationType!: GenerateSmsOperationType

	@Prop(Function)
	readonly generateSms?: () => Promise<GenerateSmsResultData | null>

	@Prop(Function)
	readonly verifySms?: (data: VerifySmsData) => Promise<boolean>

	@Prop(Boolean)
	readonly persistent?: boolean

	@Prop(Boolean)
	readonly hideSubtitle?: boolean

	@Prop(Boolean)
	readonly noFirstSms?: boolean

	@Getter('profile', { namespace: PROFILE_MODULE_NAME })
	readonly profile?: IProfileEntity

	@Getter('company', { namespace: PROFILE_MODULE_NAME })
	readonly company?: ICompany

	@Prop({ type: Number, default: DEFAULT_REMAINING_TIME })
	readonly remainingTime?: number

	smsRemainingTime: number = DEFAULT_REMAINING_TIME

	smsResendIsAvailable: boolean = false

	confirmed: boolean = false

	smsCode: string = ''

	smsCodeMask: string = MaskList.smsCode

	smsVerifyId: number = 0

    beforeDestroy() {
        this.stopTimer();
    }

	getSmsGenerateData(): GenerateSmsData {
		return {
			phone: this.company?.phone,
			flCrmId: this.profile?.flCrmId || 0,
			systemInitiator: GenerateSmsInitiator.DBOUL,
			operationType: this.operationType
		};
	}

	async generateSmsRequest(): Promise<GenerateSmsResultData | null> {
		return this.generateSms
			? this.generateSms()
			: $generateSms(this.getSmsGenerateData());
	}

	getVerifySmsData(): VerifySmsData {
		return {
			...this.getSmsGenerateData(),
			smsCode: this.smsCode,
			verifyId: this.smsVerifyId
		};
	}

	async verifySmsDataRequest(): Promise<boolean> {
		return this.verifySms
			? this.verifySms(this.getVerifySmsData())
			: $verifySms(this.getVerifySmsData());
	}

	async verifySmsCode() {
		this.blurFromInput();

		this.confirmed = await this.verifySmsDataRequest();

		if (this.confirmed) {
			this.model = false;
		} else {
			this.smsCode = '';

			this.focusToInput();
		}
	}

	onStartSmsCodeTimer() {
    	this.smsResendIsAvailable = false;
	}

	onEndSmsCodeTimer() {
		this.smsResendIsAvailable = true;
	}

	getInputElement(): HTMLInputElement | undefined {
		return this.$refs.input?.$refs.textField?.$refs.input;
	}

	focusToInput() {
		setTimeout(() => this.getInputElement()?.focus());
	}

	blurFromInput() {
		setTimeout(() => this.getInputElement()?.blur());
	}

	startTimer() {
		this.$refs.smsCode?.startCountdown();

		this.focusFieldAfterTransition(
			this.focusToInput.bind(this)
		);
	}

	stopTimer() {
		this.$refs.smsCode?.stopCountdown();
	}

    async onSmsRequest() {
		const response = await this.generateSmsRequest();

		this.smsVerifyId = response?.id || 0;

        this.startTimer();
    }

	closeDialog() {
		if (!this.persistent) this.model = false;
		else this.emitCancel();
	}

	get smsCodeMaxLength(): number {
		return this.smsCodeMask.length;
	}

	@Watch('remainingTime')
	onRemainingTimeChanged() {
		this.smsRemainingTime = this.remainingTime || DEFAULT_REMAINING_TIME;
	}

	@Watch('smsCode')
	onSmsCodeChanged() {
		this.smsCode.length === this.smsCodeMaxLength
			&& this.model
			&& this.verifySmsCode();
	}

	@Watch('model')
	onModelChanged() {
		if (!this.model) {
			this[this.confirmed ? 'emitConfirmed' : 'emitCancel']();
		} else {
			this.smsCode = '';
			this.confirmed = false;

			if (this.noFirstSms) this.startTimer();
			else this.onSmsRequest();
		}
	}

	@Emit('cancel')
	emitCancel() {}

	@Emit('confirm')
	emitConfirmed(): number {
		return this.smsVerifyId;
	}

}

